/**
 * State and rules of the reseller setup wizard. Order is easy → hard on purpose (Martin, 2026-09-24):
 * brand, services and prices, domain, and only then payment credentials, so nobody meets an API key
 * before they have something that looks like their own business.
 * Nothing here is saved yet: the tenant backend (docs/white-label/CAPABILITY-MAP.md, tenant-core) does not exist.
 */
import type { ExtraKey, PayKey, ServiceKey } from '../demo/demoData';

export type StepId = 'brand' | 'services' | 'domain' | 'payments' | 'review';
export type SiteMode = 'full' | 'dashboard';

export const STEPS: Array<{ id: Exclude<StepId, 'review'>; label: string; title: string; lede: string }> = [
  { id: 'brand', label: 'Your brand', title: 'Name your brand', lede: 'This is what your customers will see. You can change it any time.' },
  { id: 'services', label: 'Services & prices', title: 'Pick what you sell', lede: 'Switch services on, then choose how much you add on top of our price. Extras like support tickets come later in your admin.' },
  { id: 'domain', label: 'Your domain', title: 'Connect your domain', lede: 'Your customers log in on your own address. One DNS record does it.' },
  { id: 'payments', label: 'Get paid', title: 'How your customers pay you', lede: 'Money goes straight to you. Add one method now, or skip and do it later.' },
];

export const COLORS = [
  { name: 'Blue', hex: '#2563eb' },
  { name: 'Teal', hex: '#0f766e' },
  { name: 'Violet', hex: '#7c3aed' },
  { name: 'Green', hex: '#15803d' },
  { name: 'Pink', hex: '#be185d' },
  { name: 'Ink', hex: '#111827' },
];

export const MARKUPS = [50, 100, 150] as const;

// RFC 2606 example host until tenant-domains defines the real target. Visibly an example, never a live address.
export const DNS_TARGET = 'connect.example.net';

export interface PayDetails {
  fasterpayKey: string; fasterpaySecret: string;
  paypalClient: string; paypalSecret: string;
  wallet: string;
  bankHolder: string; iban: string; bic: string;
}

export interface SetupState {
  name: string;
  logo?: string;
  color: string;
  services: Record<ServiceKey, boolean>;
  extras: Record<ExtraKey, boolean>;
  markup: number;
  /** Admin overrides per package (visits → price in USD); packages without one follow the markup. */
  prices: Record<string, number>;
  /** Which of our package sizes the reseller sells (visits as strings, Professional quality). */
  offered: string[];
  mode: SiteMode;
  domain: string;
  pay: Record<PayKey, boolean>;
  /** Any other method the reseller already uses: a name plus a link or instructions shown at checkout. */
  customPay: { on: boolean; name: string; how: string };
  details: PayDetails;
  business: { company: string; country: string; email: string; vat: string };
  terms: boolean;
  /** Whether Expert quality is on sale next to Professional (same sizes as `offered`). */
  expert: boolean;
  /** SEO and YouTube packages taken off sale (catalog keys, e.g. "seo:100"). */
  hiddenPkgs: string[];
  /**
   * The reseller's credit wallets with us (admin/adminData.ts WALLETS): per wallet a warning level and an
   * optional package bought by itself below it (0 = off), so paid orders do not start to wait unnoticed.
   */
  balance: { alert: boolean; wallets: Record<string, { warnBelow: number; autoBuy: number }> };
  coupons: Coupon[];
  affiliate: { rate: number; minPayout: number; cookieDays: number };
  /** Reward levels the reseller funds: reach the spend, get the discount on every later order. */
  levels: Array<{ name: string; spend: number; discount: number }>;
  team: TeamMember[];
  /** Sender of every e-mail to the reseller's customers: "<fromName> <fromLocal@domain>". */
  mail: { fromName: string; fromLocal: string };
  /** The reseller's own terms and privacy pages, linked on their website and sign-up (https only). */
  legal: { termsUrl: string; privacyUrl: string };
}

export interface Coupon { code: string; kind: 'percent' | 'amount'; value: number; maxUses: number; used: number; expires: string; on: boolean }
export type TeamRole = 'Admin' | 'Support' | 'Viewer';
export interface TeamMember { id: string; name: string; email: string; role: TeamRole; status: 'Invited' | 'Active' }

export const INITIAL_SETUP: SetupState = {
  name: '',
  color: COLORS[0].hex,
  services: { web: true, gsc: true, local: false, yt: false },
  extras: { tickets: true, roadmap: false, rewards: false, affiliate: false },
  markup: 100,
  prices: {},
  offered: ['60000', '300000', '1000000', '3000000'],
  mode: 'full',
  domain: '',
  pay: { fasterpay: false, paypal: false, crypto: false, bank: false },
  customPay: { on: false, name: '', how: '' },
  details: { fasterpayKey: '', fasterpaySecret: '', paypalClient: '', paypalSecret: '', wallet: '', bankHolder: '', iban: '', bic: '' },
  business: { company: '', country: '', email: '', vat: '' },
  terms: false,
  expert: true,
  hiddenPkgs: [],
  balance: {
    alert: true,
    wallets: {
      professional: { warnBelow: 1000000, autoBuy: 0 }, expert: { warnBelow: 300000, autoBuy: 0 }, seo: { warnBelow: 750, autoBuy: 0 },
      yt_views: { warnBelow: 5000, autoBuy: 0 }, yt_subs: { warnBelow: 250, autoBuy: 0 }, yt_likes: { warnBelow: 1000, autoBuy: 0 }, yt_hours: { warnBelow: 1000, autoBuy: 0 },
    },
  },
  coupons: [{ code: 'WELCOME10', kind: 'percent', value: 10, maxUses: 100, used: 7, expires: '', on: true }],
  affiliate: { rate: 15, minPayout: 50, cookieDays: 30 },
  levels: [
    { name: 'Starter', spend: 0, discount: 0 },
    { name: 'Regular', spend: 250, discount: 2 },
    { name: 'Partner', spend: 1000, discount: 5 },
  ],
  team: [],
  mail: { fromName: '', fromLocal: 'support' },
  legal: { termsUrl: '', privacyUrl: '' },
};

/** Our cost plus the markup, rounded up to a price ending in 9 ($379, $49). */
export const priceFor = (cost: number, markup: number) => Math.max(9, Math.ceil((cost * (1 + markup / 100)) / 10) * 10 - 1);

/** Accepts what people type ("https://www.Nova-Visits.com/pricing") and returns the bare host. */
export function cleanDomain(input: string): string {
  return input.trim().toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/^www\./, '')
    .replace(/\.$/, '');
}

export const isDomain = (d: string) =>
  /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(d);

/**
 * The records the reseller sets: app.<domain> for the dashboard, www.<domain> for the website. The server says
 * where they point (backend tenants/service.py DNS_TARGET: an IP means an A record); the example target is
 * only for the previews.
 */
export function dnsRecords(domain: string, mode: SiteMode, target: { type: string; target: string; verify?: { host: string; value: string } | null } = { type: 'CNAME', target: DNS_TARGET }) {
  const rows = [{ type: target.type, host: 'app', value: target.target, purpose: `app.${domain} · your customer dashboard` }];
  if (mode === 'full') rows.unshift({ type: target.type, host: 'www', value: target.target, purpose: `www.${domain} · your website` });
  // Every shop points at the same address, so this record is what proves the domain is yours.
  if (target.verify) rows.push({ type: 'TXT', host: target.verify.host, value: target.verify.value, purpose: 'proves the domain is yours' });
  return rows;
}

/** White or ink, whichever reads better on the brand colour (relative luminance threshold 0.42). */
export function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#fff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.42 ? '#080a0f' : '#fff';
}

/**
 * The problem with what was entered on a step, or null. Every step can be skipped (Martin, 2026-09-24),
 * so this only runs on "Continue"; skipping never asks for anything.
 */
export function stepError(step: StepId, s: SetupState): string | null {
  if (step === 'brand' && !s.name.trim()) return 'Enter a brand name, or skip this step for now.';
  if (step === 'domain') {
    if (!s.domain) return 'Enter the domain you want to use, or skip this step for now.';
    if (!isDomain(s.domain)) return 'That does not look like a domain yet. Try something like novavisits.com.';
  }
  // Going live needs only the reseller terms (Martin, 2026-09-29: "even if I don't make all those settings
  // I still want to be able to go live"). Everything else stays on the to-do list (missing) and in the admin.
  if (step === 'review') {
    const email = s.business.email.trim();
    if (email && !/^\S+@\S+\.\S+$/.test(email)) return 'That e-mail address for invoices does not look right.';
    if (!s.terms) return 'Accept the reseller terms to go live.';
  }
  return null;
}

/** What a skipped setup still lacks, in step order. Not a condition for going live: a to-do list. */
export function missing(s: SetupState): Array<{ step: StepId; label: string }> {
  const out: Array<{ step: StepId; label: string }> = [];
  if (!s.name.trim()) out.push({ step: 'brand', label: 'Your brand name' });
  if (!isDomain(s.domain)) out.push({ step: 'domain', label: 'Your domain' });
  return out;
}

/** Whether a step holds what it asks for; a step left behind without it counts as skipped, not done. */
export function isComplete(step: StepId, s: SetupState): boolean {
  if (step === 'brand') return !!s.name.trim();
  if (step === 'domain') return isDomain(s.domain);
  if (step === 'payments') return Object.values(s.pay).some(Boolean) || s.customPay.on;
  return true;
}

/** The price a customer pays for a package: the admin's own price if set, else cost plus markup. */
export const packagePrice = (s: SetupState, visits: number, cost: number) => s.prices[String(visits)] ?? priceFor(cost, s.markup);
